import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import clsx from 'clsx';
import styles from './validation-message.module.less';
import type { TrackingProp } from '../types';
import { useId, useTracking, getLabel, getText } from '../utils';

export type ValidationMessageProps = React.ComponentPropsWithoutRef<'span'> & {
  htmlFor?: string;
  invalid?: boolean;
  tracking?: TrackingProp;
};

export const ValidationMessage = forwardRef<
  HTMLSpanElement,
  ValidationMessageProps
>(function ValidationMessage(
  { className, hidden, htmlFor, id, invalid = true, tracking = true, ...rest },
  ref
) {
  const ariaId = useId();
  const isTracked = useRef(false);
  const innerRef = useRef<HTMLSpanElement>(null);
  const classes = clsx(styles.message, invalid || styles.ok, className);
  const track = useTracking(tracking);

  useImperativeHandle(ref, () => innerRef.current as HTMLSpanElement); // Forward innerRef
  useEffect(() => {
    let target: HTMLElement | null; // Cache taret for clean up

    // Let React render first and not block thread
    window.requestAnimationFrame(() => {
      if (hidden) return;
      target = getTarget(htmlFor, innerRef.current);
      const aria = invalid ? 'aria-errormessage' : 'aria-describedby';
      const label = target instanceof HTMLInputElement && target.labels?.[0];
      const legend = target?.querySelector('legend');

      // Only track first impression
      if (!isTracked.current && (isTracked.current = true))
        track({
          eventCategory: `validationmessage_${invalid ? 'invalid' : 'success'}`,
          eventAction: 'impression',
          eventLabel: `validationmesssage_${getLabel(label || legend)}`,
          eventStatus: getText(innerRef.current)
        });

      // Connect to relevant <input>
      if (target) {
        target.setAttribute(aria, `${id || ariaId}`);
        target.setAttribute('data-validation-message-invalid', `${invalid}`);
      } else if (innerRef.current)
        console.warn(
          '<ValiationMessage> is missing related input. Please set htmlFor={inputId}.',
          innerRef.current
        );
    });

    // Clean up when removed
    return () => {
      target?.removeAttribute('aria-describedby');
      target?.removeAttribute('aria-errormessage');
      target?.removeAttribute('data-validation-message-invalid');
    };
  }, [ariaId, hidden, htmlFor, id, innerRef, invalid, track]);

  return (
    <span
      aria-live="polite"
      className={classes}
      hidden={hidden}
      id={id || ariaId}
      ref={innerRef}
      role="alert"
      {...rest}
    />
  );
});

const getTarget = (htmlFor?: string, self?: HTMLElement | null) =>
  document.querySelector<HTMLInputElement>(`input[id="${htmlFor}"]`) ||
  self?.closest('label')?.querySelector('input') ||
  self?.closest('fieldset') ||
  null;
